/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
    Spinner,
    Heading,
    Panel,
    DisplayHtmlContent,
    ContentContainer,
    FlexRow,
    FlexCol,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
    Alert,
} from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import ErrorNotification from 'ui-permit/src/components/ErrorNotification';
import LotteryWinnerPrompt from './LotteryWinnerPrompt';

const descriptionContent = [
    { name: 'Need to Know', contentPath: 'need_to_know_permit' },
    { name: 'Fee Policy', contentPath: 'fee_policy' },
    { name: 'Cancellation Policy', contentPath: 'cancellation_policy' },
    { name: 'No Show Policy', contentPath: 'no_show_policy' },
];

function LotteryWinnerConfirmation(props) {
    const {
        isLoading,
        errors,
        isValidAwardedApp,
        permit,
        lottery,
        recareaName,
        lotteryApplication,
        isAccepting,
        isDeclining,
        handleLotteryAccept,
        handleLotteryDecline,
    } = props;

    const [isModalOpen, setIsModalOpen] = useState();

    const handleLotteryDeclineWithModal = () => {
        if (!isModalOpen) setIsModalOpen(true);
    };

    const renderDescriptionContent = () => {
        return descriptionContent.map((c) => (
            <Panel label="Need to Know" key={c.name}>
                <p>
                    <strong>{c.name}</strong>
                </p>
                <DisplayHtmlContent html={permit?.description?.[c.contentPath]} />
            </Panel>
        ));
    };

    if (isLoading) {
        return (
            <div className="rec-page">
                <div className="permit-content-outer-wrap">
                    <Spinner />
                </div>
            </div>
        );
    }

    // Don't display rest of page if hasErrors AND either permit or issuance did not load
    if (errors?.length && (!permit.id || !lotteryApplication.id)) {
        return (
            <div className="rec-page">
                <ErrorNotification errorHeader="Page Unavailable" errors={errors} />
            </div>
        );
    }

    if (!isValidAwardedApp) {
        return (
            <div className="rec-page">
                <div aria-live="polite">
                    <Alert
                        type="error"
                        heading="Warning - this page has expired."
                        hasAriaLive
                    />
                </div>
            </div>
        );
    }

    const address = permit?.addresses?.[0];
    let permitLocation = address?.state || '';
    if (address?.city) {
        permitLocation = `${address?.city}, ${permitLocation}`;
    }

    // ex. permitType is stored as turkey-hunt-lottery on lotteryApplication but stored as turkey-hunt on permit
    const permitType = get(permit, 'permit_types_list', []).filter(
        (pt) =>
            pt.id === get(lotteryApplication, 'permit_type', '').replace('-lottery', '')
    );

    return (
        <div className="rec-page lottery-winner-confirmation-page">
            <StyledModal
                role="complementary"
                contentLabel="Decline Reservation"
                heading="Decline Reservation"
                size="md"
                onRequestClose={() => setIsModalOpen(!isModalOpen)}
                isOpen={isModalOpen}
            >
                <div className="rec-modal-content-wrap">
                    <p>
                        Are you sure you want to decline your lottery win? This action
                        cannot be undone.
                    </p>
                </div>
                <ModalActions>
                    <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                        <Button
                            appearance="tertiary"
                            onClick={() => setIsModalOpen(!isModalOpen)}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleLotteryDecline}>Ok</Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>

            {!!permit.id && (
                <ContentContainer>
                    <FlexRow>
                        <FlexCol md={8}>
                            <section className="permit-content-main">
                                <div id="page-body">
                                    {!!errors?.length && (
                                        <ErrorNotification errors={errors} />
                                    )}
                                    <Heading
                                        appearance="h3"
                                        headingLevel={1}
                                        className="mb-4"
                                    >
                                        Reservation Confirmation
                                    </Heading>
                                    <Heading
                                        appearance="h6"
                                        headingLevel={2}
                                        className="mb-5"
                                    >
                                        You have been awarded a reservation for a permit!
                                    </Heading>
                                    <p className="permit-text-margin">
                                        Please confirm that you will accept or decline the
                                        reservation.
                                    </p>
                                    <hr className="my-5" />
                                    {renderDescriptionContent()}
                                </div>
                            </section>
                        </FlexCol>
                        <FlexCol md={4}>
                            <section className="permit-content-aside">
                                <LotteryWinnerPrompt
                                    isAccepting={isAccepting}
                                    isDeclining={isDeclining}
                                    handleLotteryAccept={handleLotteryAccept}
                                    handleLotteryDecline={handleLotteryDeclineWithModal}
                                    orderId={lotteryApplication?.order_id}
                                    permitName={permit?.name}
                                    permitLocation={permitLocation}
                                    permitTypeName={permitType?.name}
                                    recareaName={recareaName}
                                    divisionID={lotteryApplication?.division_id}
                                    startDate={gnoment(
                                        lotteryApplication?.extra_fields
                                            ?.selected_lottery_preference?.preferred_date
                                    )
                                        .utc()
                                        .startOf('day')
                                        .format('MM/DD/YYYY')}
                                    confirmDate={gnoment
                                        .unix(lottery?.lottery?.deadline_at?.seconds)
                                        .tz(permit?.time_zone)
                                        .format('MM/DD/YYYY [at] hh:mm a z')}
                                />
                            </section>
                        </FlexCol>
                    </FlexRow>
                </ContentContainer>
            )}
        </div>
    );
}

LotteryWinnerConfirmation.propTypes = {
    isAccepting: PropTypes.bool,
    isDeclining: PropTypes.bool,
    isLoading: PropTypes.bool,
    errors: PropTypes.array,
    isValidAwardedApp: PropTypes.bool,
    permit: PropTypes.object /*HuntingPermitType*/,
    lottery: PropTypes.object /*LotteryType*/,
    lotteryApplication: PropTypes.object /*HuntingLotteryIssuanceType*/,
    recareaName: PropTypes.string,
    handleLotteryAccept: PropTypes.func,
    handleLotteryDecline: PropTypes.func,
};

export default LotteryWinnerConfirmation;
